import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import React, { useMemo } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";

import Navigation from "./Navigation";
import { ObjectSource, ObjectSourceCategory, isAObjectSourceCategory } from "./sources/types";
import { makeRequest } from "./util";
import { DownwardArrowIcon, EditIcon } from "./components/icons/themes/windows10";
import { useDispatch, useSelector } from "react-redux";
import { fetchObjectSources } from "./actions/objectSources";
import { StoreState } from "./store";
import { allDestinationsData, DestinationDefinition } from "./destinations/destinations";
import { DestinationSlug } from "./destinations/types";
import { CustomDestinationOption, DestinationSelect } from "./components/DestinationSelect";
import { LoadedDestinationInfo } from "./types/global";
import { useLocalStorage } from "react-use";
import { ConfiguredObjectSource, ObjectSourceSetupState } from "./reducers/objectSources";
import { useAlert } from "react-alert";
import useAccountMetadata from "./hooks/useAccountMetadata";

const useTableStyles = makeStyles((theme) => ({
    content: {
        padding: 0,
        "&:last-child": {
            paddingBottom: 0,
        },
    },
    inner: {
        minWidth: 700,
    },
    switchContainerHidden: {
        visibility: "hidden",
    },
    card: {
        width: "65%",
        minWidth: 800,
    },
    clickableImg: {
        maxHeight: 40,
        "&:hover": {
            cursor: "pointer",
        },
    },
    navigation: {
        paddingTop: 24,
    },
    sourceInfo: {
        display: "flex",
        alignItems: "center",
    },
    sourceName: {
        color: "#444",
        fontSize: 16,
        verticalAlign: "center",
        marginLeft: 8,
    },
    tableRow: {
        "&:hover": {
            backgroundColor: "#f0f0f0",
            cursor: "pointer",
            "& button": {
                backgroundColor: "rgba(63, 81, 181, 0.08)",
            },
        },
    },
    tableCell: {
        borderBottom: "none",
        alignItems: "center",
    },
}));

enum ObjectSourceState {
    NOT_SETUP,
    INCOMPLETE,
    CONNECTED,
    BROKEN,
}

const convertConfigStateToSourceState = (configuredObjectSource: ConfiguredObjectSource | undefined): ObjectSourceState => {
    if (configuredObjectSource) {
        switch (configuredObjectSource.setup_state) {
            case ObjectSourceSetupState.BROKEN:
                return ObjectSourceState.BROKEN;
            case ObjectSourceSetupState.INCOMPLETE:
                return ObjectSourceState.INCOMPLETE;
            case ObjectSourceSetupState.CONNECTED:
                return ObjectSourceState.CONNECTED;
        }
    }
    return ObjectSourceState.NOT_SETUP;
}

const ObjectSourceRowButton: React.FC<{
    source: ObjectSource;
    selectedOption: CustomDestinationOption;
}> = ({source, selectedOption}) => {
    const alert = useAlert();
    const warehouseGroupID = useMemo(() => selectedOption.value, [selectedOption]);
    const [opening, setOpening] = React.useState(false);
    const objectSourceConfigs: ConfiguredObjectSource[] = useSelector((state: StoreState) => state.objectSources.configured);
    const configuredObjectSource = useMemo(() => objectSourceConfigs.find((config) => {
        return config.service === source.fivetranService && config.groupID === warehouseGroupID;
    }), [objectSourceConfigs, warehouseGroupID, source]);

    // Determine the state of the object source.
    const currentState = useMemo(() => {
        return convertConfigStateToSourceState(configuredObjectSource);
    }, [configuredObjectSource]);

    const openConnectorCard = React.useCallback(async () => {
        setOpening(true);

        try {
            const response = await makeRequest<{
                id: string;
                callback_url: string;
            }>("fivetran_connector_card_url", {
                operation: "create",
                service: source.fivetranService,
                warehouseGroup: warehouseGroupID,
                fivetranID: configuredObjectSource ? configuredObjectSource.fivetranID : null,
                callbackUrl: window.location.href,
            });

            // @ts-ignore
            window.location.href = response.data.callback_url;
        } catch (e) {
            alert.error("Something went wrong");
        } finally {
            setOpening(false);
        }
    }, [configuredObjectSource, warehouseGroupID, source]);

    const buttonProps = {
        // Annoying `as` type enforcement to prevent type widening
        variant: "text" as "text",
        color: "primary" as "default",
        size: "small" as "small",
        onClick: openConnectorCard,
        disabled: opening,
    }

    if (currentState === ObjectSourceState.NOT_SETUP || currentState === ObjectSourceState.INCOMPLETE || currentState === ObjectSourceState.BROKEN) {
        return (
            <Button {...buttonProps}>
                {buttonProps.disabled ? "Loading..." : "Configure"}
            </Button>
        );
    } else {
        return (
            <Button {...buttonProps} startIcon={<EditIcon/>}>
                {buttonProps.disabled ? "Loading..." : "Modify"}
            </Button>
        );
    }
}

const ObjectSourcesRow: React.FC<{
    source: ObjectSource;
    subitem?: boolean;
    selectedOption: CustomDestinationOption | undefined;
}> = ({source, subitem = false, selectedOption}) => {
    const classes = useTableStyles();
    const SourceIcon = source.logo;

    return (
        <TableRow className={classes.tableRow}>
            <TableCell className={classes.tableCell}>
                <div className={clsx(classes.sourceInfo, {
                    'pl-10': subitem
                })}>
                    {SourceIcon && <SourceIcon height={42} width={42}/>}
                    <span className={clsx(classes.sourceName, {
                        'pl-5': subitem
                    })}>{source.name}</span>
                </div>
            </TableCell>
            <TableCell align="center" className={classes.tableCell}>
                {selectedOption && (
                    <ObjectSourceRowButton source={source} selectedOption={selectedOption}/>
                )}
            </TableCell>
        </TableRow>
    );
};

const ObjectSourcesCategory: React.FC<{
    source: ObjectSourceCategory;
    selectedOption: CustomDestinationOption | undefined;
}> = ({source, selectedOption}) => {
    const classes = useTableStyles();
    const SourceIcon = source.logo;

    return (
        <>
            <TableRow>
                <TableCell className={classes.tableCell}>
                    <div className={classes.sourceInfo}>
                        {SourceIcon && <SourceIcon height={42} width={42}/>}
                        <span className={clsx(classes.sourceName)}>{source.name}</span>
                        <DownwardArrowIcon height={10} width={10} className="ml-2 mt-1"/>
                    </div>
                </TableCell>
                <TableCell align="center" className={classes.tableCell}/>
            </TableRow>
            {source.items.map((itemSource) => <ObjectSourcesRow
                subitem={true}
                source={itemSource}
                selectedOption={selectedOption}
                key={itemSource.name}/>
            )}
        </>
    );
};

function ObjectSourcesPage() {
    const classes = useTableStyles();
    const dispatch = useDispatch();
    const [timer, setTimer] = React.useState<NodeJS.Timeout>();
    const {
        data: allConfiguredDestinations,
        loading: destinationsLoading
    } = useSelector((state: StoreState) => state.destinations);
    const accountMetadata = useAccountMetadata();
    const {allowedObjectSources} = accountMetadata.config;
    const {choices: objectSourceChoices, configured: configuredObjectSources, loading: objectSourcesLoading} = useSelector((state: StoreState) => state.objectSources);
    const [availableDestinationOptions, setAvailableDestinationsOptions] = React.useState<CustomDestinationOption[]>([]);
    const [selectedDestinationID, setSelectedDestinationID] = useLocalStorage<string | undefined>("fp_object_destination", undefined);

    const selectedOption = React.useMemo(() => {
        let opt;
        if (!Array.isArray(availableDestinationOptions) || availableDestinationOptions.length === 0) {
            return;
        }

        if (selectedDestinationID) {
            opt = availableDestinationOptions.find((dest) => dest.value === selectedDestinationID)
        }

        if (!opt) {
            opt = availableDestinationOptions[0];
        }
        return opt;
    }, [selectedDestinationID, availableDestinationOptions]);

    const convertDestinationToSelectOption = React.useCallback((loadedDest: LoadedDestinationInfo, destData: DestinationDefinition): CustomDestinationOption => {
        const Icon = destData.logoSquare;
        return {
            label: loadedDest.name,
            icon: <Icon/>,
            value: loadedDest.fivetran_group_id,
        }
    }, []);

    React.useEffect(() => {
        if (destinationsLoading) {
            return;
        }

        const warehouses: typeof availableDestinationOptions = [];
        for (let slug of (Object.keys(allConfiguredDestinations) as Array<DestinationSlug>)) {
            const configuredDestination = (allConfiguredDestinations[slug] as LoadedDestinationInfo);

            if (allConfiguredDestinations[slug] === undefined) {
                continue;
            }

            const destinationData = allDestinationsData[slug] as DestinationDefinition;
            if (destinationData.type === "warehouse" && configuredDestination.enabled && configuredDestination.fivetran_group_id) {
                if (!selectedDestinationID) {
                    // If theres no selected option yet, set it to the first enabled warehouse
                    setSelectedDestinationID(configuredDestination.id);
                }

                warehouses.push(convertDestinationToSelectOption(configuredDestination, destinationData));
            }
        }

        setAvailableDestinationsOptions(warehouses);
    }, [allConfiguredDestinations, destinationsLoading]);

    const refreshObjectSources = React.useCallback(() => dispatch(fetchObjectSources()), [dispatch]);
    const selectObjectSourceDestination = React.useCallback((option) => setSelectedDestinationID(option.value), [setSelectedDestinationID]);

    React.useEffect(() => {
        refreshObjectSources(); // Run once upon load.
        setTimer(setInterval(refreshObjectSources, (1000 * 60 * 30))); // Run every 30mins

        return () => {
            if (timer) {
                clearInterval(timer)
            }
        }
    }, []);

    // Memoize the filtered & sorted order of the choices
    const filteredObjectSourceChoices = useMemo(() => {
        if (objectSourcesLoading) {
            return objectSourceChoices;
        }

        const allowedSet = new Set(allowedObjectSources);
        let choices = objectSourceChoices;

        if (allowedObjectSources && allowedObjectSources.length >= 0) {
            const serviceIsAllowed = (choice: ObjectSource) => allowedSet.has(choice.fivetranService);
            const categoryIsAllowed = (choiceCategory: ObjectSourceCategory) => choiceCategory.items.filter(serviceIsAllowed).length > 0;

            const filteredChoices = [];

            // objectSourceChoices is a list of the choices for a react-select component
            for (let objectSourceChoice of objectSourceChoices) {
                 if (isAObjectSourceCategory(objectSourceChoice)) {
                     if (categoryIsAllowed(objectSourceChoice)) {
                         const copy = {...objectSourceChoice, items: objectSourceChoice.items.filter(serviceIsAllowed)};
                         filteredChoices.push(copy)
                     }
                 } else {
                     if (serviceIsAllowed(objectSourceChoice)) {
                         filteredChoices.push(objectSourceChoice)
                     }
                 }
            }

            choices = filteredChoices;
        }


        // Sorting makes it feel kinda weird. Maybe revisit later. For now just return the filtered choices
        return choices
    }, [allowedObjectSources, objectSourceChoices, objectSourcesLoading])

    return (
        <Navigation className={clsx(classes.navigation)}>
            <div className={clsx(classes.card)}>
                <Typography component="h2" gutterBottom variant="overline">
                    Objects
                </Typography>

                <div className="flex justify-between mb-4">
                    <Typography component="h1" gutterBottom variant="h3">
                        Connect non-event sources directly to your warehouse
                    </Typography>

                    <DestinationSelect
                        className="w-1/4"
                        onChange={selectObjectSourceDestination}
                        options={availableDestinationOptions}
                        value={selectedOption}
                    />
                </div>

                <Card>
                    <CardHeader title="Sources"/>
                    <Divider/>
                    <CardContent className={clsx(classes.content)}>
                        <PerfectScrollbar>
                            <div className={clsx(classes.inner)}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Integration</TableCell>
                                            <TableCell align="center">Configure</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredObjectSourceChoices.map((source) =>
                                            source.hasOwnProperty("items") ? (
                                                <ObjectSourcesCategory
                                                    selectedOption={selectedOption}
                                                    key={source.name}
                                                    source={(source as ObjectSourceCategory)}
                                                />
                                            ) : (
                                                <ObjectSourcesRow
                                                    selectedOption={selectedOption}
                                                    key={source.name}
                                                    source={(source as ObjectSource)}
                                                />
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </PerfectScrollbar>
                    </CardContent>
                </Card>
            </div>
        </Navigation>
    );
}

export default ObjectSourcesPage;
